import { Component, DestroyRef, ViewChild } from '@angular/core';
import { AddUserForm } from "../../../../forms/add-user/add-user.form";
import { InfoModalComponent } from '../../../../parts/info-modal/info-modal.component';
import { BootstrapService } from '../../../../core/services/bootstrap.service';
import { ModalActions, ModalTypes } from '../../../../core/enums/base.enums';
import { ConfirmationModalComponent } from "../../../../parts/confirmation-modal/confirmation-modal.component";
import { ServiceResultModel } from '../../../../core/models/service/service-result.model';
import { UserModel } from '../../../../core/models/data/user.model';
import { GenericMessagingConstants } from '../../../../core/constants/messaging.constants';
import { EscCloseDirective } from '../../../../core/directives/esc-close.directive';
import { BaseFormComponent } from '../../../../core/base-classes/base-form-component';
import { CanComponentDeactivate } from '../../../../core/routing/can-deactivate.guard';
import { DirtyFormRegistryService } from '../../../../core/services/dirty-form-registry.service';

@Component({
  selector: 'ms-add-user',
  imports: [AddUserForm, InfoModalComponent, ConfirmationModalComponent, EscCloseDirective],
  templateUrl: './add-user.component.html',
})
export class AddUserComponent extends BaseFormComponent implements CanComponentDeactivate {
  private resetFormOnConfirm : boolean = true;

  @ViewChild(AddUserForm) addUserFormRef!: AddUserForm;
  @ViewChild('userFormConfirmationModalRef') userFormConfirmationModalRef!: ConfirmationModalComponent;
  @ViewChild('userFormInfoModalRef') userFormInfoModalRef!: InfoModalComponent;

  public confirmationConfirmFunction: Function = () => {};
  public confirmationCancelFunction: Function = () => {};

  constructor(
    private bootstrapService: BootstrapService,
    private dirtyFormRegistry: DirtyFormRegistryService,
    private destroyRef: DestroyRef
  ) {
    super();
    this.destroyRef.onDestroy(() => this.dirtyFormRegistry.unregister(this));
  }

  public onCancelRequested(): void {
    this.setConfirmationModalFunctions();

    if (this.addUserFormRef.addUserForm.dirty) {
      this.userFormConfirmationModalRef.modalType.set(ModalTypes.Question);

      this.userFormConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());

      this.bootstrapService.manageModal("#userFormConfirmationModal", ModalActions.Show);
      this.bootstrapService.manageModal("#addUserModal", ModalActions.Hide);
    } else {
      this.bootstrapService.manageModal("#addUserModal", ModalActions.Hide);
      this.dirtyFormRegistry.unregister(this);
    }
  }

  public canDeactivate(): boolean | Promise<boolean> {
    return this.awaitLeaveConfirmation(this.addUserFormRef.addUserForm.dirty && !this.submitSucceeded, () => {
      this.setConfirmationModalFunctions();
      this.userFormConfirmationModalRef.modalType.set(ModalTypes.Question);
      this.userFormConfirmationModalRef.message.set(GenericMessagingConstants.GENERIC_ABORT_CONFIRMATION());
      this.bootstrapService.manageModal("#addUserModal", ModalActions.Hide);
      this.bootstrapService.manageModal("#userFormConfirmationModal", ModalActions.Show);
    });
  }

  public openAddUserModal(): void {
    this.submitSucceeded = false;
    this.dirtyFormRegistry.register(this);
    this.bootstrapService.manageModal("#addUserModal", ModalActions.Show);
  }

  public confirmationModalConfirmed(): void {
    this.addUserFormRef.resetForm();
    this.bootstrapService.manageModal("#userFormConfirmationModal", ModalActions.Hide);
    this.dirtyFormRegistry.unregister(this);
    this.resolvePendingLeave(true);
  }

  public infoModalConfirmed(): void {
    if (this.resetFormOnConfirm === true) {
      this.submitSucceeded = false;
      this.addUserFormRef.resetForm();
      this.dirtyFormRegistry.unregister(this);
    }
  }

  public onAddUserResult(result: ServiceResultModel<UserModel>): void {
    this.resetFormOnConfirm = result.success!;

    this.userFormInfoModalRef.message.set(result.message);

    if (result.success === true) {
      this.submitSucceeded = true;
      this.userFormInfoModalRef.modalType.set(ModalTypes.Success);
      this.bootstrapService.manageModal("#addUserModal", ModalActions.Hide);
    } else {
      this.userFormInfoModalRef.modalType.set(ModalTypes.Failure);
    }

    this.bootstrapService.manageModal("#userFormInfoModal", ModalActions.Show);
  }

  public confirmationModalCancelled(): void {
    this.bootstrapService.manageModal("#addUserModal", ModalActions.Show);
    this.bootstrapService.manageModal("#userFormConfirmationModal", ModalActions.Hide);
    this.resolvePendingLeave(false);
  }

  public setConfirmationModalFunctions() : void {
    this.confirmationConfirmFunction = this.confirmationModalConfirmed;
    this.confirmationCancelFunction = this.confirmationModalCancelled;
  }
}
